import { useState, type ReactNode } from "react";
import { genres as genreOptions, regions as regionOptions, years as yearOptions } from "@/mocks/movies";
import type { FilterState, SortKey } from "@/utils/movies";

interface FilterBarProps {
  filters: FilterState;
  onChange: (next: FilterState) => void;
  onReset: () => void;
  total: number;
}

const sortOptions: { key: SortKey; label: string }[] = [
  { key: "hot", label: "热门" },
  { key: "new", label: "最新" },
  { key: "rating", label: "评分" },
];

interface ChipProps {
  active: boolean;
  label: string;
  onClick: () => void;
}

function Chip({ active, label, onClick }: ChipProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`cursor-pointer whitespace-nowrap rounded-full border px-3 py-1.5 text-xs transition-colors md:text-sm ${
        active
          ? "border-primary-500 bg-primary-500 text-background-50"
          : "border-background-200 bg-background-50 text-foreground-600 hover:border-primary-300 hover:text-primary-600"
      }`}
    >
      {label}
    </button>
  );
}

interface FilterRowProps {
  label: string;
  children: ReactNode;
}

function FilterRow({ label, children }: FilterRowProps) {
  return (
    <div className="flex flex-col gap-2 py-3 sm:flex-row sm:items-start sm:gap-4">
      <span className="w-14 shrink-0 pt-1 text-sm font-medium text-foreground-500">{label}</span>
      <div className="flex flex-wrap gap-2">{children}</div>
    </div>
  );
}

export default function FilterBar({ filters, onChange, onReset, total }: FilterBarProps) {
  const [sheetOpen, setSheetOpen] = useState(false);

  const toggleGenre = (genre: string) => {
    if (genre === "全部") {
      onChange({ ...filters, genres: ["全部"] });
      return;
    }
    const withoutAll = filters.genres.filter((g) => g !== "全部");
    const next = withoutAll.includes(genre)
      ? withoutAll.filter((g) => g !== genre)
      : [...withoutAll, genre];
    onChange({ ...filters, genres: next.length ? next : ["全部"] });
  };

  const activeCount =
    (filters.region !== "全部" ? 1 : 0) +
    (filters.year !== "全部" ? 1 : 0) +
    (filters.genres.filter((g) => g !== "全部").length || 0);

  const controls = (
    <div className="divide-y divide-background-200">
      <FilterRow label="地区">
        {regionOptions.map((region) => (
          <Chip
            key={region}
            label={region}
            active={filters.region === region}
            onClick={() => onChange({ ...filters, region })}
          />
        ))}
      </FilterRow>
      <FilterRow label="年份">
        {yearOptions.map((year) => (
          <Chip
            key={year}
            label={year}
            active={filters.year === year}
            onClick={() => onChange({ ...filters, year })}
          />
        ))}
      </FilterRow>
      <FilterRow label="类型">
        {genreOptions.map((genre) => (
          <Chip
            key={genre}
            label={genre}
            active={filters.genres.includes(genre) || (genre === "全部" && filters.genres.length === 0)}
            onClick={() => toggleGenre(genre)}
          />
        ))}
      </FilterRow>
      <FilterRow label="排序">
        {sortOptions.map((option) => (
          <Chip
            key={option.key}
            label={option.label}
            active={filters.sort === option.key}
            onClick={() => onChange({ ...filters, sort: option.key })}
          />
        ))}
      </FilterRow>
    </div>
  );

  return (
    <>
      {/* Desktop filter panel */}
      <div className="hidden rounded-lg border border-background-200 bg-background-50 px-5 md:block">
        {controls}
        <div className="flex items-center justify-between border-t border-background-200 py-3">
          <span className="text-sm text-foreground-500">
            共找到 <strong className="font-label font-bold text-accent-600">{total}</strong> 部作品
          </span>
          <button
            type="button"
            onClick={onReset}
            className="inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border border-background-200 px-3 py-1.5 text-sm text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-600"
          >
            <i className="ri-refresh-line" aria-hidden="true" />
            重置筛选
          </button>
        </div>
      </div>

      {/* Mobile filter trigger */}
      <div className="flex items-center justify-between gap-3 md:hidden">
        <span className="text-sm text-foreground-500">
          共 <strong className="font-label font-bold text-accent-600">{total}</strong> 部作品
        </span>
        <button
          type="button"
          onClick={() => setSheetOpen(true)}
          className="inline-flex h-10 cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 text-sm font-medium text-background-50"
        >
          <i className="ri-filter-3-line text-lg" aria-hidden="true" />
          筛选{activeCount > 0 ? `·${activeCount}` : ""}
        </button>
      </div>

      {/* Mobile bottom sheet */}
      {sheetOpen ? (
        <div className="fixed inset-0 z-[70] md:hidden" role="dialog" aria-modal="true">
          <button
            type="button"
            aria-label="关闭筛选"
            onClick={() => setSheetOpen(false)}
            className="absolute inset-0 h-full w-full cursor-pointer bg-foreground-950/50"
          />
          <div className="absolute inset-x-0 bottom-0 max-h-[80vh] overflow-y-auto rounded-t-2xl bg-background-50 px-4 pb-6 pt-4">
            <div className="mb-2 flex items-center justify-between">
              <h3 className="font-heading text-base font-bold text-foreground-950">筛选条件</h3>
              <button
                type="button"
                onClick={() => setSheetOpen(false)}
                className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-foreground-600 hover:bg-background-100"
                aria-label="关闭"
              >
                <i className="ri-close-line text-xl" aria-hidden="true" />
              </button>
            </div>
            {controls}
            <div className="mt-4 flex gap-3">
              <button
                type="button"
                onClick={() => {
                  onReset();
                  setSheetOpen(false);
                }}
                className="h-11 flex-1 cursor-pointer rounded-md border border-background-200 text-sm font-medium text-foreground-700"
              >
                重置
              </button>
              <button
                type="button"
                onClick={() => setSheetOpen(false)}
                className="h-11 flex-1 cursor-pointer rounded-md bg-primary-500 text-sm font-medium text-background-50"
              >
                查看 {total} 个结果
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}